import type { ProjectBox, ProjectDetail } from '../api/types'
import { config } from '../config'
import { workSiteLine } from '../lib/projectLabel'
import { QrCode } from './QrCode'

/**
 * Mærkatet på en sagskasse (beslutning 143 og 145).
 *
 * <b>Sager har deres egen sti (`/s/`)</b> - ikke enhedernes `/e/`, lagerstedernes `/l/`,
 * varernes `/v/` eller værktøjets `/m/`. Mærkatet scannes med telefonens eget kamera,
 * og så åbnes den URL, der står i koden. Sagen har ingen kort kode som de andre; den
 * bruger sit id, som alle sager har fra starten. Adgangen afgøres som altid af sagen selv.
 *
 * <b>Hver kasse har sit eget mærkat</b>: `/s/<sag>/<kassenr>`. Kasse 1 bruger `/s/<sag>`, så
 * de mærkater, der blev printet før der kunne være flere kasser, stadig passer.
 */

/** URL'en der ender i QR-koden på en sagskasse. */
export function caseUrl(projectId: number, boxNumber = 1) {
  const base = (config.publicUrl || window.location.origin).replace(/\/$/, '')
  return boxNumber === 1 ? `${base}/s/${projectId}` : `${base}/s/${projectId}/${boxNumber}`
}

/**
 * Adressen på mærkatet: sagens egen, ellers kundens - samme regel som kalenderens
 * `site` (arbejdet foregår der, hvor sagen siger, ellers hos kunden).
 */
export function caseLabelAddress(detail: Pick<ProjectDetail, 'project' | 'site'>): string | null {
  const own = workSiteLine(detail.project)
  if (own) return own

  const site = detail.site
  if (!site?.address) return null
  const town = [site.postalCode, site.city].filter(Boolean).join(' ')
  return [site.address, town].filter(Boolean).join(', ')
}

/**
 * Ét mærkat, som det ser ud på papir. Samme opsætning som værktøjets mærkat
 * (`device-label`): QR-koden til venstre og teksten til højre, så det printer ens på
 * Brother-rullen - Simon så, at værktøjets mærkat kom rigtigt ud, og det første
 * sagskasse-mærkat (QR i midten) ikke gjorde (2026-10-29).
 */
export function CaseLabel({
  detail,
  box
}: {
  detail: Pick<ProjectDetail, 'project' | 'site'>
  box: Pick<ProjectBox, 'number' | 'name'>
}) {
  const { project } = detail
  const address = caseLabelAddress(detail)

  return (
    <div className="device-label">
      <QrCode value={caseUrl(project.id, box.number)} size={132} />

      <div className="device-label__text">
        <div className="device-label__name">{project.orderNumber ? `Ordre ${project.orderNumber}` : project.name}</div>
        {/* Kassens nummer stort - det er det, man kigger efter på hylden. Ikke `device-label__code`:
            den er skrevet til 8 tegn i fast bredde. */}
        <div className="device-label__line" style={{ fontWeight: 800, fontSize: 14 }}>
          Kasse {box.number}
        </div>
        {box.name && (
          <div className="device-label__line" style={{ fontWeight: 700 }}>
            {box.name}
          </div>
        )}
        {/* Adressen må fylde to linjer - på rullen er tekstfeltet kun godt 2 cm bredt. */}
        {address && (
          <div className="device-label__line case-label__address" style={{ marginTop: 2 }}>
            {address}
          </div>
        )}
      </div>
    </div>
  )
}
